import { Avatar } from '@mui/material';
import { LoadingSpinner, Stack } from 'components';
import dayjs from 'dayjs';
import { getLsUserRoleObject } from 'modules/networkTools/localStorageTokens';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getClientNote,
  getFavoriteZCodes,
  getSites
} from 'store/clientProfile/clientProfileActions';
import {
  getAllCHWs,
  getAllManagers,
  getBillingProviders,
  getClient,
  getClientHistory
} from 'store/clients/clientsActions';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { useFetchAvatarImage } from './hooks/useFetchAvatarImage';
import { NoteEdit } from './noteEdit';
import Transcript from './noteEdit/transcript/transcript';
import NoteHeader from './noteHeader';
import { NoteHistory } from './noteHistory';
import style from './notePopup.module.scss';
import NoteView from './noteView';

const NotePopup = () => {
  const { noteId, clientId } = useParams();

  const { clientNotes } = useAppSelector((state) => state.clientProfile);
  const { data: client } = useAppSelector((state) => state.clients.client);
  const dispatch = useAppDispatch();
  const { organizationId } = getLsUserRoleObject();
  const note = clientNotes[noteId ?? '']?.data;
  const [loading, setLoading] = useState(false);
  const createdByAvatarImage = useFetchAvatarImage(note?.created?.profileImageKey);
  const updatedByAvatarImage = useFetchAvatarImage(note?.updated?.profileImageKey);

  const getNote = async () => {
    if (!clientId || !organizationId || !noteId) {
      return;
    }

    setLoading(true);

    await Promise.all([
      dispatch(
        getClientNote({
          clientId,
          noteId
        })
      ),
      dispatch(getClient({ clientId, organizationId })),
      dispatch(getClientHistory({ clientId })),
      dispatch(getAllManagers({ organizationId })),
      dispatch(getAllCHWs({ organizationId })),
      dispatch(getBillingProviders({ organizationId })),
      dispatch(getFavoriteZCodes()),
      dispatch(getSites())
    ]);

    setLoading(false);
  };

  useEffect(() => {
    getNote();
  }, []);

  if (loading || client == null || note == null) {
    return (
      <Stack width="100%" display="flex" flexDirection="row" justifyContent="center" mt="64px">
        <LoadingSpinner />
      </Stack>
    );
  }

  return (
    <Stack width="100%" pb="16px" display="flex" boxSizing="border-box">
      <Stack boxSizing="border-box" overflow="auto" flexGrow={1}>
        <NoteHeader client={client} note={note} />
        <Stack
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          px="20px"
          mt="16px"
        >
          <Stack display="flex" flexDirection="row" gap="8px" alignItems="center">
            <p className={style.createdBy}>Created by:</p>
            <Stack gap="4px" display="flex" flexDirection="row" alignItems="center">
              <Avatar
                sx={{
                  width: 16,
                  height: 16
                }}
                alt="Avatar of the user"
                src={createdByAvatarImage}
              >
                <span className="material-icons-outlined">person</span>
              </Avatar>
              <p className={style.authorName}>{note.created?.name}</p>
            </Stack>
          </Stack>
          <p className={style.date}>{dayjs(note?.created.timestamp).format('MM/DD/YYYY')}</p>
        </Stack>
        <Stack
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          px="20px"
          mt="16px"
        >
          <Stack display="flex" flexDirection="row" gap="8px" alignItems="center">
            <p className={style.createdBy}>{note.draft ? 'Last updated by:' : 'Finalized by:'}</p>
            <Stack gap="4px" display="flex" flexDirection="row" alignItems="center">
              <Avatar
                sx={{
                  width: 16,
                  height: 16
                }}
                alt="Avatar of the user"
                src={updatedByAvatarImage}
              >
                <span className="material-icons-outlined">person</span>
              </Avatar>
              <p className={style.authorName}>{note.updated?.name}</p>
            </Stack>
          </Stack>
          <p className={style.date}>{dayjs(note?.updated.timestamp).format('MM/DD/YYYY')}</p>
        </Stack>
        <Stack px="20px">
          {note.draft && clientId ? (
            <NoteEdit noteId={noteId} note={note} clientId={clientId} context="popup" inputOpen />
          ) : (
            <>
              <NoteView note={note} />
              <NoteHistory note={note} />
            </>
          )}
          {note?.aiScribe?.transcript && <Transcript note={note} />}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default NotePopup;
